import { Add, Delete } from "@mui/icons-material";
import { Box, Button, IconButton, MenuItem, TextField } from "@mui/material";
import { createServiceDescriptions } from "api/services/serviceDescription";
import DialogWrapper from "components/DialogWrapper";
import LoadingButton from "components/LoadingButton";
import SafeTextField from "components/SafeTextField";
import { snack } from "components/toast";
import { TAXES } from "data/taxes";
import _ from "lodash";
import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import { DialogProps, SubmitType } from "types";
import { handleError } from "utils/handleError";

interface ServiceDescriptionRow {
  description: string;
  hsnSac: string;
  gstRate: string;
  serviceFee: string;
}

interface StateProps {
  serviceDescriptions: Array<ServiceDescriptionRow>;
}

const emptyRow: ServiceDescriptionRow = { description: "", hsnSac: "", gstRate: "", serviceFee: "" };

const initialState: StateProps = {
  serviceDescriptions: [{ ...emptyRow }],
};

const HSN_SAC_REGEX = /^\d{2}$|^\d{4}$|^\d{6}$/;
const isValidHsnSac = (value: string) => HSN_SAC_REGEX.test(value.trim());

function AddServiceDescription({ open, setOpen }: DialogProps) {
  const queryClient = useQueryClient();
  const [state, setState] = useState<StateProps>(_.cloneDeep(initialState));

  useEffect(() => {
    if (open) {
      setState(_.cloneDeep(initialState));
    }
  }, [open]);

  const { mutate, isLoading } = useMutation(createServiceDescriptions, {
    onSuccess: () => {
      snack.success("Service Description Added");
      setState(_.cloneDeep(initialState));
      setOpen(false);
      queryClient.invalidateQueries("service-descriptions");
    },
    onError: (err: any) => {
      snack.error(handleError(err));
    },
  });

  const handleItemChange = (e: any, index: number) => {
    const newItems: any = [...state.serviceDescriptions];
    newItems[index][e.target.name] = e.target.value;
    setState({ ...state, serviceDescriptions: newItems });
  };

  const addRow = () => {
    setState({
      ...state,
      serviceDescriptions: [...state.serviceDescriptions, { ...emptyRow }],
    });
  };

  const deleteRow = (index: number) => {
    const newRows = [...state.serviceDescriptions];
    newRows.splice(index, 1);
    setState({
      ...state,
      serviceDescriptions: newRows,
    });
  };

  const handleSubmit = (e: SubmitType) => {
    e.preventDefault();
    const hasInvalidRow = state.serviceDescriptions.some(
      (item) => !item.description.trim() || !`${item.serviceFee}`.trim()
    );
    if (hasInvalidRow) {
      return snack.error("Please fill Service Description and Service Fee for all rows");
    }
    const hasInvalidHsnSac = state.serviceDescriptions.some(
      (item) => item.hsnSac.trim() && !isValidHsnSac(item.hsnSac)
    );
    if (hasInvalidHsnSac) {
      return snack.error("HSN/SAC must be exactly 2, 4 or 6 digits");
    }
    mutate({ serviceDescriptions: state.serviceDescriptions });
  };

  return (
    <DialogWrapper
      open={open}
      setOpen={setOpen}
      title="Add Service Description"
      width="lg"
      actionButtons={
        <LoadingButton
          loading={isLoading}
          disabled={isLoading}
          loadingColor="white"
          width={16}
          size="small"
          title="Submit"
          color="secondary"
          type="submit"
          form="add-service-description-form"
        />
      }
    >
      <form id="add-service-description-form" onSubmit={handleSubmit}>
        {state.serviceDescriptions.map((item, index) => (
          <Box display="flex" gap={2} mb={2} alignItems="center" key={index}>
            <Box flex={2}>
              <SafeTextField
                variant="outlined"
                fullWidth
                size="small"
                name="description"
                multiline
                rows={2}
                value={item.description}
                onChange={(e) => handleItemChange(e, index)}
                label="Service Description"
                required
                inputProps={{ maxLength: 250 }}
              />
            </Box>
            <Box flex={1}>
              <SafeTextField
                variant="outlined"
                fullWidth
                size="small"
                name="hsnSac"
                value={item.hsnSac}
                onChange={(e) => handleItemChange(e, index)}
                label="HSN/SAC"
                allowedPattern={/0-9/}
                inputProps={{ maxLength: 6 }}
                error={!!item.hsnSac && !isValidHsnSac(item.hsnSac)}
                helperText={
                  !!item.hsnSac && !isValidHsnSac(item.hsnSac) ? "Must be 2, 4 or 6 digits" : ""
                }
              />
            </Box>
            <Box flex={1}>
              <TextField
                select
                variant="outlined"
                fullWidth
                size="small"
                name="gstRate"
                value={item.gstRate}
                onChange={(e) => handleItemChange(e, index)}
                label="GST Rate"
              >
                {TAXES.map((tax) => (
                  <MenuItem key={tax.value} value={tax.value}>
                    {tax.name}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
            <Box flex={1}>
              <SafeTextField
                variant="outlined"
                fullWidth
                size="small"
                type="number"
                name="serviceFee"
                value={item.serviceFee}
                onChange={(e) => handleItemChange(e, index)}
                onWheel={(e) => (e.target as HTMLElement).blur()}
                label="Service Fee"
                required
                inputProps={{ min: 0, max: 10000000, step: "0.01" }}
              />
            </Box>
            <Box>
              <IconButton
                onClick={() => deleteRow(index)}
                disabled={state.serviceDescriptions.length === 1}
              >
                <Delete />
              </IconButton>
            </Box>
          </Box>
        ))}
        <Box textAlign="right" mt={1} mb={2}>
          <Button onClick={addRow} color="secondary" startIcon={<Add />}>
            Add More
          </Button>
        </Box>
      </form>
    </DialogWrapper>
  );
}

export default AddServiceDescription;
